import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useOrgProjects } from '../../lib/useOrgProjects.js';
import { Card } from '../../components/ui/Card.js';

/**
 * Org-wide blocker summary: every project the current user can see, ranked by its
 * `counters.byStatus.blocked` count (see docs/PLAN.md §3). Reuses the same
 * `memberUids array-contains uid` query as ProjectsListPage — the projects/{id} read rule
 * needs that filter present for Firestore to prove the query is safe.
 */
export function BlockersWidget() {

  const { data: projects, loading } = useOrgProjects();

  const blocked = useMemo(
    () =>
      projects
        .map((p) => ({ project: p, blockedCount: p.counters?.byStatus?.blocked ?? 0 }))
        .filter(({ project, blockedCount }) => project.status === 'active' && blockedCount > 0)
        .sort((a, b) => b.blockedCount - a.blockedCount),
    [projects],
  );

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <span className="text-xs font-medium">Blockers</span>
        {blocked.length > 0 && (
          <span className="rounded-full bg-[color-mix(in_srgb,var(--danger)_15%,transparent)] px-2 py-0.5 text-[10px] font-medium text-danger">
            {blocked.length} project{blocked.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loading ? (
        <p className="p-4 text-sm text-muted">Loading…</p>
      ) : blocked.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted">No blockers — nice.</p>
      ) : (
        <div className="flex flex-col divide-y divide-border">
          {blocked.map(({ project, blockedCount }) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}/tasks`}
              className="flex items-center gap-3 p-3 transition-colors hover:bg-surface-2"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-danger" />
              <span className="flex-1 truncate text-sm">{project.name}</span>
              <span className="font-mono text-xs font-semibold text-danger">{blockedCount}</span>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
